import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Plus, Database, Trash2, Edit, ChevronRight, Download, Upload } from 'lucide-react'
import type { SystemSkillDatabase } from '@creanizer/types'
import { useSystemSkillStore } from '../../../../stores/systemSkillStore'

interface SkillDatabaseListProps {
  onSelectDatabase: (id: string) => void
  selectedDatabaseId: string | null
}

export function SkillDatabaseList({ onSelectDatabase, selectedDatabaseId }: SkillDatabaseListProps) {
  const { databases, addDatabase, removeDatabase, exportAsJSON, importFromJSON } = useSystemSkillStore()
  const [showAddForm, setShowAddForm] = useState(false)
  const [newSystemName, setNewSystemName] = useState('')
  const [newVersion, setNewVersion] = useState('')
  const [importError, setImportError] = useState('')

  const handleAddDatabase = () => {
    if (!newSystemName.trim()) return
    addDatabase({
      systemName: newSystemName.trim(),
      version: newVersion.trim() || undefined,
      skills: []
    })
    setNewSystemName('')
    setNewVersion('')
    setShowAddForm(false)
  }

  const handleExport = () => {
    const json = exportAsJSON()
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `skill-databases-${new Date().toISOString().slice(0, 10)}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setImportError('')

    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        importFromJSON(event.target?.result as string)
      } catch (error) {
        setImportError('インポートに失敗しました')
      }
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="w-5 h-5" />
          スキルデータベース
        </CardTitle>
        <CardDescription>
          TRPGシステムのスキルを登録・管理します
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* ツールバー */}
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => setShowAddForm(true)}>
            <Plus className="w-4 h-4 mr-1" />新規DB
          </Button>
          <Button size="sm" variant="outline" onClick={handleExport}>
            <Download className="w-4 h-4 mr-1" />エクスポート
          </Button>
          <label className="cursor-pointer inline-flex items-center justify-center rounded-md text-sm font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3">
            <Upload className="w-4 h-4 mr-1" />インポート
            <input type="file" accept=".json" onChange={handleImport} className="hidden" />
          </label>
        </div>

        {importError && (
          <Alert variant="destructive"><AlertDescription>{importError}</AlertDescription></Alert>
        )}

        {/* 新規追加フォーム */}
        {showAddForm && (
          <div className="p-4 border rounded-lg space-y-3 bg-gray-50">
            <div>
              <Label>システム名</Label>
              <Input
                value={newSystemName}
                onChange={(e) => setNewSystemName(e.target.value)}
                placeholder="例: クトゥルフ神話TRPG"
                className="mt-1"
              />
            </div>
            <div>
              <Label>バージョン（オプション）</Label>
              <Input
                value={newVersion}
                onChange={(e) => setNewVersion(e.target.value)}
                placeholder="例: 7版"
                className="mt-1"
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAddDatabase}>作成</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowAddForm(false)}>
                キャンセル
              </Button>
            </div>
          </div>
        )}

        {/* データベース一覧 */}
        <div className="space-y-2">
          {databases.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              データベースがありません。「新規DB」で作成してください。
            </p>
          ) : (
            databases.map(db => (
              <div
                key={db.id}
                className={`p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                  selectedDatabaseId === db.id ? 'border-blue-500 bg-blue-50' : ''
                }`}
                onClick={() => onSelectDatabase(db.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Database className="w-4 h-4 text-gray-500" />
                    <span className="font-medium">{db.systemName}</span>
                    {db.version && <Badge variant="secondary">{db.version}</Badge>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{db.skills.length}件</Badge>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); removeDatabase(db.id) }}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  )
}
